/**
 * ClientMessageThread — the ONE reusable 2-way conversation view (SLIK Connect).
 *
 * Embeds everywhere: the Connect center, the appointment detail card, the Unassigned panel,
 * the client profile. Give it the client's context; it subscribes to the thread (keyed by
 * phone-digits), renders bubbles, and sends via communicationService.sendToClient (→ toll-free
 * SMS + logged). Inbound replies arrive live once the smsInbound webhook is wired on the number.
 *
 * UI is functional/clean — the design polish pass comes AFTER the infrastructure (Jose 2026-09-25).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { communicationService, Message } from '../../services/communicationService';
import { CONNECT_TEMPLATES, ConnectTemplate } from '../../shared/connectTemplates';
import { renderTemplate } from '../../shared/messagingConfig';
import { ConnectSource, sourceMeta, labelFor } from '../../shared/connectSources';

interface Props {
  clientId?: string;
  clientName: string;
  phone?: string;
  appointmentId?: string;
  /** Quick templates (chips above the composer). Strings = send-on-tap; objects = full ConnectTemplate. */
  templates?: Array<string | ConnectTemplate>;
  /** Source tag for messages typed HERE (defaults to 'manual' = a staff message). */
  source?: ConnectSource;
  /** Staff member who's typing (→ "Message from Kelly" on the bubble). */
  sentByName?: string;
  className?: string;
}

// Normalize a string|ConnectTemplate to a ConnectTemplate (bare strings are send-on-tap acks).
const asTemplate = (t: string | ConnectTemplate): ConnectTemplate =>
  typeof t === 'string' ? { label: t, body: t } : t;

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** "Today" / "Yesterday" / "Mar 4" (year only when not this year) — the day-separator label. */
const dayLabel = (d: Date): string => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const that = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.round((today.getTime() - that.getTime()) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: today.getFullYear() === d.getFullYear() ? undefined : 'numeric',
  });
};

const ClientMessageThread: React.FC<Props> = ({ clientId, clientName, phone, appointmentId, templates = CONNECT_TEMPLATES, source = 'manual', sentByName, className = '' }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // A chip tap: fill the merge fields we KNOW ({client}/{firstName}/{shop}); the rest stay as {…} for staff.
  // Outreach templates (fillThenEdit) load into the composer to finish; short acks send immediately.
  const firstName = (clientName || '').split(' ')[0] || '';
  const applyTemplate = (t: ConnectTemplate) => {
    const text = renderTemplate(t.body, { client: firstName, firstName, shop: 'SLIK Barber Lounge', senderName: 'SLIK Barber Lounge' });
    if (t.fillThenEdit) {
      setDraft(text);
      setTimeout(() => { const el = textareaRef.current; if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); } }, 0);
    } else {
      send(text);
    }
  };

  const phoneDigits = useMemo(() => String(phone || '').replace(/\D/g, '').slice(-10), [phone]);

  useEffect(() => {
    if (!phoneDigits) return;
    const unsub = communicationService.subscribeToThread(phoneDigits, setMessages);
    communicationService.markThreadRead(phoneDigits);
    return () => unsub();
  }, [phoneDigits]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const send = async (text: string) => {
    const body = text.trim();
    if (!body || !phone || sending) return;
    setSending(true);
    setDraft('');
    await communicationService.sendToClient({ clientId, clientName, phone, appointmentId, body, source, sentByName });
    setSending(false);
  };

  if (!phone) {
    return (
      <div className={`flex items-center justify-center h-full text-sm text-gray-400 p-6 text-center ${className}`}>
        No phone number on file for {clientName}. Add one to their profile to message them.
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full min-h-0 bg-gray-50 dark:bg-gray-900 ${className}`}>
      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-2">
        {messages.length === 0 && (
          <div className="text-center text-xs text-gray-400 mt-6">No messages yet. Say hello 👋</div>
        )}
        {messages.map((m, i) => {
          const out = m.direction !== 'in';
          const prev = messages[i - 1];
          const showDay = !prev || !sameDay(prev.sentAt, m.sentAt);
          return (
            <React.Fragment key={m.id}>
              {showDay && (
                <div className="flex justify-center my-3">
                  <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 bg-gray-200/70 dark:bg-gray-800 px-2.5 py-0.5 rounded-full">
                    {dayLabel(m.sentAt)}
                  </span>
                </div>
              )}
              <div className={`flex ${out ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                    out
                      ? 'bg-purple-600 text-white rounded-br-md'
                      : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-bl-md'
                  }`}
                >
                  <div className="whitespace-pre-wrap break-words">{m.content}</div>
                  <div className={`text-[10px] mt-0.5 flex items-center gap-1.5 flex-wrap ${out ? 'justify-end text-purple-200' : 'text-gray-400'}`}>
                    {/* SOURCE LABEL (Jose 2026-10-30): every message says where it came from — Waitlist,
                        Receipt, Reminder, "Message from Kelly", Client reply… */}
                    <span className={`px-1.5 py-[1px] rounded-full font-medium text-[9px] ${out ? 'bg-white/20 text-white/90' : sourceMeta(m.source, m.direction).tone}`}>
                      {labelFor(m.source, m.direction, m.sentByName)}
                    </span>
                    <span>{m.sentAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                    {out && m.status === 'failed' && <span className="text-red-200 font-semibold">· failed</span>}
                    {out && (m.status === 'pending' || m.status === 'queued') && <span>· sending…</span>}
                    {out && m.status === 'suppressed' && <span>· not sent (test)</span>}
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Quick templates — short acks send on tap; outreach (✎) loads into the composer to fill in */}
      {templates.length > 0 && (
        <div className="flex gap-2 px-3 pt-2 pb-1 overflow-x-auto shrink-0 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
          {templates.map((raw, i) => {
            const t = asTemplate(raw);
            return (
              <button
                key={i}
                onClick={() => applyTemplate(t)}
                disabled={sending}
                title={t.fillThenEdit ? 'Load into composer to fill in, then send' : 'Send'}
                className="shrink-0 text-xs px-3 py-1.5 rounded-full bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-700 hover:bg-purple-100 whitespace-nowrap touch-manipulation disabled:opacity-50"
              >
                {t.fillThenEdit && <span className="opacity-60 mr-1">✎</span>}
                {t.label.length > 34 ? t.label.slice(0, 32) + '…' : t.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Composer */}
      <div className="flex items-end gap-2 p-3 shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(draft); }
          }}
          rows={1}
          placeholder={`Message ${clientName?.split(' ')[0] || ''}…`}
          className="flex-1 resize-none max-h-28 rounded-2xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 px-4 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
        <button
          onClick={() => send(draft)}
          disabled={sending || !draft.trim()}
          className="shrink-0 w-11 h-11 rounded-full bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center disabled:opacity-40 touch-manipulation active:scale-95"
          title="Send"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default ClientMessageThread;
